import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar } from 'lucide-react';
import axios from 'axios';
import Header from './Header';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

function PostList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/posts`).then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Header />
      <main className="pt-24 pb-20 min-h-screen" data-testid="journal-page">
        <div className="max-w-[800px] mx-auto px-6 md:px-12">
          <motion.p className="section-number mb-3" {...fadeUp} data-testid="journal-label">
            Journal
          </motion.p>
          <motion.h1
            className="section-title mb-4"
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="journal-title"
          >
            Thinking Out Loud.
          </motion.h1>
          <motion.p
            className="text-[var(--cv-text-dim)] text-sm md:text-base mb-14"
            style={{ fontFamily: 'var(--font-body)', lineHeight: 1.7 }}
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            Short-form writing on ideas, strategy, AI, and the work we do.
          </motion.p>

          {loading && <p className="text-[var(--cv-text-dim)] text-sm">Loading...</p>}

          {!loading && posts.length === 0 && (
            <motion.div
              className="py-16 text-center"
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              data-testid="journal-empty"
            >
              <p className="text-[var(--cv-text-dim)] text-sm mb-4" style={{ fontFamily: 'var(--font-body)' }}>
                Nothing published yet. Check back soon.
              </p>
              <Link to="/" className="nav-link text-xs" data-testid="journal-back-link">
                Back to Home
              </Link>
            </motion.div>
          )}

          <div className="flex flex-col gap-0" data-testid="journal-list">
            {posts.map((post, i) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to={`/journal/${post.slug}`}
                  className="block py-6 border-b border-[var(--cv-border)] group transition-all hover:pl-2"
                  data-testid={`journal-post-${post.slug}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar size={12} className="text-[var(--cv-text-dim)]" />
                    <span
                      className="text-xs text-[var(--cv-text-dim)]"
                      style={{ fontFamily: 'var(--font-mono)' }}
                    >
                      {new Date(post.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                  <h2
                    className="text-lg md:text-xl font-bold text-white group-hover:text-[var(--cv-text-dim)] transition-colors"
                    style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}
                  >
                    {post.title}
                  </h2>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="mt-12"
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Link to="/" className="nav-link text-xs" data-testid="journal-home-link">
              &larr; Back to Home
            </Link>
          </motion.div>
        </div>
      </main>
    </>
  );
}

function PostDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/posts/${slug}`).then((r) => setPost(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--cv-text-dim)]">Loading...</div>;
  if (!post) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-[var(--cv-text-dim)]">Post not found.</p>
      <Link to="/journal" className="nav-link text-xs">Back to Journal</Link>
    </div>
  );

  return (
    <>
      <Header />
      <main className="pt-24 pb-20 min-h-screen" data-testid="journal-post-page">
        <article className="max-w-[700px] mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <Link to="/journal" className="nav-link text-xs inline-flex items-center gap-1.5 mb-8" data-testid="post-back-link">
              <ArrowLeft size={12} /> Journal
            </Link>
          </motion.div>

          <motion.div
            className="flex items-center gap-2 mb-4"
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <Calendar size={12} className="text-[var(--cv-text-dim)]" />
            <span className="text-xs text-[var(--cv-text-dim)]" style={{ fontFamily: 'var(--font-mono)' }}>
              {new Date(post.created_at).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric',
              })}
            </span>
          </motion.div>

          <motion.h1
            className="text-3xl md:text-4xl font-bold mb-10 leading-tight"
            style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.03em' }}
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.15 }}
            data-testid="post-title"
          >
            {post.title}
          </motion.h1>

          <motion.div
            className="journal-body"
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.25 }}
            data-testid="post-body"
          >
            {post.body.split('\n\n').map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </motion.div>

          <motion.div
            className="mt-14 pt-8 border-t border-[var(--cv-border)]"
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Link to="/journal" className="nav-link text-xs">&larr; Back to Journal</Link>
          </motion.div>
        </article>
      </main>
    </>
  );
}

export { PostList, PostDetail };
